import { TFunction } from '@mint-lib/i18n';

import {
  CellFormatType,
  ValidatedCell,
  type CellErrorType,
  type GranularityLevel,
} from '../api/types.js';
import { getErrorMessage } from '../utils/api.js';

export const getUploadFileErrorMessage = (t: TFunction, error: unknown) => {
  const uploadFileErrorsMap: Record<string, string> = {
    CellSizeExceedError: t('Cell size limit reached: Max 255 symbols allowed.'),
    ColumnsExceedError: t('Columns number exceeded: Max 100 columns allowed.'),
    EmptyFileError: t('Empty file: Upload a file with data.'),
    RowExceedError: t('Rows number exceeded: Max 50,000 rows allowed.'),
    TooManySheetsError: t(
      'The file exceeds the limit of 60 sheets. Please upload another file.',
    ),
  };
  const errorMessage = getErrorMessage(t, error, uploadFileErrorsMap);

  return errorMessage;
};

export const getCellErrorMessage = ({
  t,
  cell,
  formatType,
  granularityLevel,
}: {
  t: TFunction;
  cell: ValidatedCell;
  formatType: CellFormatType;
  granularityLevel?: GranularityLevel;
}) => {
  const errorType = cell.error;

  if (!errorType) {
    return null;
  }

  if (errorType === 'type_required') {
    return t(
      'To continue, ensure this cell is filled with the necessary information',
    );
  }

  type ValidationErrorsByFields = Partial<
    Record<CellFormatType, Partial<Record<CellErrorType, string>>>
  >;
  const validationErrorsMap: ValidationErrorsByFields = {
    id: {
      type_invalid: t(
        'The data in this cell cannot be processed due to a data type mismatch',
      ),
      type_not_found:
        granularityLevel === 'platform_campaign'
          ? t(
              "The provided Campaign ID couldn't be found within the available records",
            )
          : t(
              "The provided Placement folder ID couldn't be found within the available records",
            ),
    },
    date: {
      type_invalid: t(
        'The data in this cell cannot be processed due to a data type mismatch',
      ),
      type_not_involved:
        granularityLevel === 'media_row'
          ? t('Date does not correspond to the to the Media row dates')
          : t(
              'Date does not correspond to the media plan dates within the selected campaign.',
            ),
    },
    platform: {
      type_invalid: t('The Platform is not recognized'),
      type_not_involved: t(
        'The Platform is not included in the chosen Campaign',
      ),
      type_not_found: t(
        'The Platform is not associated with the selected Channel',
      ),
    },
    channel: {
      type_invalid: t('The Channel is not recognized'),
      type_not_involved: t(
        'The Channel is not included in the chosen Campaign',
      ),
      type_not_found: t(
        'The Channel is not associated with the selected Platform',
      ),
    },
    media_row: {
      type_invalid: t(
        'Media row name is not associated with the selected platform',
      ),
    },
  } as const;

  const customError = validationErrorsMap[formatType]?.[errorType];
  if (customError) {
    return customError;
  }

  if (errorType === 'type_invalid') {
    return t(
      'The data in this cell cannot be processed due to a data type mismatch',
    );
  }
  return t('Unknown error');
};

export const getGlossaryAliasesErrorMessage = (
  t: TFunction,
  error: unknown,
) => {
  const errorsMap: Record<string, string> = {
    default: t('Unable to process alias request. Please try again later.'),
    CodeDuplicateError: t('Alias with this name already exists.'),
    CodeCountExceededError: t(
      'The alias cannot be added as the maximum number of aliases reached.',
    ),
    CodeUsedInImportError: t(
      'This alias cannot be deleted as in use in a draft file.',
    ),
  };

  const errorMessage = getErrorMessage(t, error, errorsMap);

  return errorMessage;
};

export const getAutoImportErrorData = (t: TFunction, error: string) => {
  const errorsMap: Record<string, { title: string; message: string }> = {
    file_not_attached: {
      title: t('No file was attached in the email.'),
      message: t('Please upload a file to proceed.'),
    },
    file_is_empty: {
      title: t('The file attached in the email was empty.'),
      message: t('Please upload a file with data to proceed.'),
    },
    file_size_exceeded: {
      title: t('The file attached in the email is too large.'),
      message: t('Please upload a smaller file to proceed.'),
    },
    file_format_incorrect: {
      title: t('The file format of the attachment in the email is incorrect.'),
      message: t('Please upload a file in the correct format to proceed.'),
    },
    multiple_files_attached: {
      title: t('Multiple files were attached in the email.'),
      message: t('Please upload only one file to proceed.'),
    },
  };

  return errorsMap[error] || {};
};
